import { z } from 'zod';
import { refersTo } from '../common/refers-to';
import { DOCUMENT_MAX_SIZE_BYTES } from '../property/documents.schemas';
import { DataFieldKeySchema, type DataFieldKey } from './document-template-data-fields';

/**
 * The kinds of document a Vorlage produces (IMM-270). The tokens are the IMM-218
 * document categories, so IMM-268 files a generated document under its
 * Vorlage's own category, and the labels come from the same dictionary.
 */
export const DOCUMENT_TEMPLATE_CATEGORIES = ['BROKER_CONTRACT', 'POWER_OF_ATTORNEY'] as const;
export const DocumentTemplateCategorySchema = z.enum(DOCUMENT_TEMPLATE_CATEGORIES);
export type DocumentTemplateCategory = z.infer<typeof DocumentTemplateCategorySchema>;

export const DOCUMENT_TEMPLATE_NAME_MAX_LENGTH = 80;
export const DOCUMENT_TEMPLATE_FOOTER_MAX_LENGTH = 200;

export const MarkSchema = z.object({ type: z.enum(['bold', 'italic', 'underline']) });
export const TextNodeSchema = z.object({
  type: z.literal('text'),
  text: z.string().min(1),
  marks: z.array(MarkSchema).optional(),
});
export const HardBreakNodeSchema = z.object({ type: z.literal('hardBreak') });

/** A placed data field (chip): which catalogue entry, and whether generation needs a value (PRD `## Fields → Placed data field`). */
export const DataFieldNodeSchema = z.object({
  type: z.literal('dataField'),
  attrs: z.object({ key: DataFieldKeySchema, required: z.boolean() }),
});

const InlineNodeSchema = z.discriminatedUnion('type', [TextNodeSchema, HardBreakNodeSchema, DataFieldNodeSchema]);
const ParagraphNodeSchema = z.object({ type: z.literal('paragraph'), content: z.array(InlineNodeSchema).optional() });
const HeadingNodeSchema = z.object({
  type: z.literal('heading'),
  attrs: z.object({ level: z.union([z.literal(1), z.literal(2), z.literal(3)]) }),
  content: z.array(InlineNodeSchema).optional(),
});

export type ParagraphNode = z.infer<typeof ParagraphNodeSchema>;
export type BulletListNode = { type: 'bulletList'; content: ListItemNode[] };
export type OrderedListNode = {
  type: 'orderedList';
  attrs?: { start?: number | undefined } | undefined;
  content: ListItemNode[];
};
export type ListItemNode = { type: 'listItem'; content: Array<ParagraphNode | BulletListNode | OrderedListNode> };

// The lists and their items refer to one another; each is a module-level const
// and the cycle closes through `z.lazy`, so every walker meets the same instances.
const ListItemNodeSchema: z.ZodType<ListItemNode> = z.object({
  type: z.literal('listItem'),
  content: z
    .array(z.union([ParagraphNodeSchema, z.lazy(() => BulletListNodeSchema), z.lazy(() => OrderedListNodeSchema)]))
    .min(1),
});
const BulletListNodeSchema: z.ZodType<BulletListNode> = z.object({
  type: z.literal('bulletList'),
  content: z.array(ListItemNodeSchema).min(1),
});
const OrderedListNodeSchema: z.ZodType<OrderedListNode> = z.object({
  type: z.literal('orderedList'),
  attrs: z.object({ start: z.number().int().min(1).optional() }).optional(),
  content: z.array(ListItemNodeSchema).min(1),
});

// No merged cells and no column widths (PRD Detailed Business Rule „Tables are plain grids“).
const TableCellAttrsSchema = z
  .object({ colspan: z.literal(1).optional(), rowspan: z.literal(1).optional(), colwidth: z.null().optional() })
  .optional();
const TableCellNodeSchema = z.object({
  type: z.enum(['tableCell', 'tableHeader']),
  attrs: TableCellAttrsSchema,
  // No table and no page break inside a cell (no nested tables, PRD).
  content: z
    .array(z.union([ParagraphNodeSchema, HeadingNodeSchema, BulletListNodeSchema, OrderedListNodeSchema]))
    .min(1),
});
const TableRowNodeSchema = z.object({ type: z.literal('tableRow'), content: z.array(TableCellNodeSchema).min(1) });
const TableNodeSchema = z
  .object({ type: z.literal('table'), content: z.array(TableRowNodeSchema).min(1) })
  .refine(
    (table) => new Set(table.content.map((row) => row.content.length)).size === 1,
    'every row has the same number of cells',
  );
const PageBreakNodeSchema = z.object({ type: z.literal('pageBreak') });

export const DocumentTemplateContentSchema = z.object({
  type: z.literal('doc'),
  content: z
    .array(
      z.union([
        ParagraphNodeSchema,
        HeadingNodeSchema,
        BulletListNodeSchema,
        OrderedListNodeSchema,
        TableNodeSchema,
        PageBreakNodeSchema,
      ]),
    )
    .min(1),
});
export type DocumentTemplateContent = z.infer<typeof DocumentTemplateContentSchema>;

/** The empty document Tiptap starts from. */
export const EMPTY_DOCUMENT_TEMPLATE_CONTENT: DocumentTemplateContent = {
  type: 'doc',
  content: [{ type: 'paragraph' }],
};

type WalkNode = {
  readonly type: string;
  readonly attrs?: unknown;
  readonly content?: ReadonlyArray<WalkNode> | undefined;
};

/** Every `dataField` node in document order, walking tables and lists (IMM-268 resolves chips this way). */
export function collectDataFields(
  content: DocumentTemplateContent,
): ReadonlyArray<{ readonly key: DataFieldKey; readonly required: boolean }> {
  const fieldsIn = (node: WalkNode): ReadonlyArray<{ readonly key: DataFieldKey; readonly required: boolean }> =>
    node.type === 'dataField'
      ? [DataFieldNodeSchema.shape.attrs.parse(node.attrs)]
      : (node.content ?? []).flatMap(fieldsIn);

  return fieldsIn(content);
}

export const DocumentTemplatesPathParamsSchema = z.object({ workspaceId: z.uuid() });
export const DocumentTemplatePathParamsSchema = z.object({ workspaceId: z.uuid(), documentTemplateId: z.uuid() });

export const DocumentTemplateSummarySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  category: DocumentTemplateCategorySchema,
  // The last save; set to the creation time on create, so never null.
  updatedAt: z.iso.datetime(),
  createdByMemberId: refersTo('member', z.uuid()),
  // The creator's first and last name; null when the member's user has none recorded.
  createdByName: z.string().nullable(),
});
export type DocumentTemplateSummary = z.infer<typeof DocumentTemplateSummarySchema>;

export const ListDocumentTemplatesResponseSchema = z.object({ data: z.array(DocumentTemplateSummarySchema) });
export type ListDocumentTemplatesResponse = z.infer<typeof ListDocumentTemplatesResponseSchema>;

export const DocumentTemplateSchema = DocumentTemplateSummarySchema.extend({
  content: DocumentTemplateContentSchema,
  // Plain text, one line, at most 200 characters; "" when the Vorlage has none.
  footer: z.string(),
});
export type DocumentTemplate = z.infer<typeof DocumentTemplateSchema>;

const TemplateNameSchema = z.string().trim().min(1).max(DOCUMENT_TEMPLATE_NAME_MAX_LENGTH);
// One line: the editor's field is an <input>, and the Word import joins footer paragraphs with a space.
const TemplateFooterSchema = z
  .string()
  .trim()
  .max(DOCUMENT_TEMPLATE_FOOTER_MAX_LENGTH)
  .regex(/^[^\r\n]*$/);

export const CreateDocumentTemplateBodySchema = z
  .object({
    category: DocumentTemplateCategorySchema,
    name: TemplateNameSchema,
    content: DocumentTemplateContentSchema,
    footer: TemplateFooterSchema,
  })
  .strict();
export type CreateDocumentTemplateBody = z.infer<typeof CreateDocumentTemplateBodySchema>;

// No category and no name: the category is fixed at creation (AC 18), and renaming is not in the PRD.
export const UpdateDocumentTemplateBodySchema = z
  .object({ content: DocumentTemplateContentSchema, footer: TemplateFooterSchema })
  .strict();
export type UpdateDocumentTemplateBody = z.infer<typeof UpdateDocumentTemplateBodySchema>;

/** A Vorlage's JSON body may be large (a 40-page contract with tables); the routes raise Fastify's 1 MiB default to this. */
export const DOCUMENT_TEMPLATE_BODY_LIMIT_BYTES = 5 * 1024 * 1024;

/** The text one suggestion request may carry, summed over every TEXT block and TABLE cell. */
export const FIELD_SUGGESTION_TEXT_MAX_CHARACTERS = 80_000;

const BlockIdSchema = z.string().regex(/^b\d{1,5}$/);

export const FieldSuggestionBlockSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('TEXT'), id: BlockIdSchema, text: z.string().max(10_000) }),
  z.object({
    kind: z.literal('TABLE'),
    id: BlockIdSchema,
    // rows × cells of plain text; a cell's paragraphs joined with "\n"; "" for an empty cell.
    rows: z
      .array(z.array(z.string().max(2_000)).min(1).max(30))
      .min(1)
      .max(300),
  }),
]);
export type FieldSuggestionBlock = z.infer<typeof FieldSuggestionBlockSchema>;

/** Characters in UTF-16 units, the unit of the suggestion offsets: every TEXT block's text plus every TABLE cell. */
export function suggestionTextLength(blocks: ReadonlyArray<FieldSuggestionBlock>): number {
  return blocks.reduce(
    (sum, block) =>
      sum +
      (block.kind === 'TEXT'
        ? block.text.length
        : block.rows.reduce((rowSum, row) => rowSum + row.reduce((cellSum, cell) => cellSum + cell.length, 0), 0)),
    0,
  );
}

export const SuggestDocumentTemplateFieldsBodySchema = z
  .object({
    category: DocumentTemplateCategorySchema,
    blocks: z.array(FieldSuggestionBlockSchema).min(1).max(5_000),
  })
  .strict()
  .refine((body) => new Set(body.blocks.map((block) => block.id)).size === body.blocks.length, 'block ids are unique')
  .refine((body) => suggestionTextLength(body.blocks) <= FIELD_SUGGESTION_TEXT_MAX_CHARACTERS, 'text too long');
export type SuggestDocumentTemplateFieldsBody = z.infer<typeof SuggestDocumentTemplateFieldsBodySchema>;

const CellSchema = z.object({ row: z.number().int().nonnegative(), column: z.number().int().nonnegative() });

export const FieldSuggestionSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('PASSAGE'),
    blockId: z.string(),
    // null for a TEXT block; the cell for a passage inside a table.
    cell: CellSchema.nullable(),
    // UTF-16 offsets into the block's text (or the cell's), end exclusive.
    start: z.number().int().nonnegative(),
    end: z.number().int().positive(),
    key: DataFieldKeySchema,
  }),
  z.object({ kind: z.literal('EMPTY_CELL'), blockId: z.string(), cell: CellSchema, key: DataFieldKeySchema }),
]);
export type FieldSuggestion = z.infer<typeof FieldSuggestionSchema>;

export const SuggestDocumentTemplateFieldsResponseSchema = z.object({
  // COMPLETE: every part answered. PARTIAL: some parts failed. FAILED: none answered.
  outcome: z.enum(['COMPLETE', 'PARTIAL', 'FAILED']),
  // Document order; no two suggestions share or overlap a target.
  suggestions: z.array(FieldSuggestionSchema),
});
export type SuggestDocumentTemplateFieldsResponse = z.infer<typeof SuggestDocumentTemplateFieldsResponseSchema>;

/** The import's upload ceiling: the document-upload limit (PRD Resolved Decision 10). */
export const DOCUMENT_TEMPLATE_IMPORT_MAX_BYTES = DOCUMENT_MAX_SIZE_BYTES;
